import React, { useState } from 'react';
import { FileCode, ArrowRight, Check, Building2, User, MapPin, Loader2, Plus, X, ChevronLeft } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usersApi, teamsApi } from '../services/creatorApi';
import { toast } from 'sonner';
type Step = 'welcome' | 'user' | 'team' | 'regions' | 'review';
interface OnboardingData {
  userName: string;
  userEmail: string;
  teamName: string;
  regions: string[];
}
export function Onboarding() {
  const {
    identifyUser,
    completeOnboarding
  } = useWorkspace();
  const [step, setStep] = useState<Step>('welcome');
  const [data, setData] = useState<OnboardingData>({
    userName: '',
    userEmail: '',
    teamName: '',
    regions: []
  });
  const [newRegion, setNewRegion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleNext = () => {
    if (step === 'welcome') setStep('user');else if (step === 'user') {
      if (data.userName && data.userEmail) setStep('team');
    } else if (step === 'team') {
      if (data.teamName) setStep('regions');
    } else if (step === 'regions') setStep('review');
  };
  const handleBack = () => {
    if (step === 'user') setStep('welcome');else if (step === 'team') setStep('user');else if (step === 'regions') setStep('team');else if (step === 'review') setStep('regions');
  };
  const handleAddRegion = (e: React.FormEvent) => {
    e.preventDefault();
    if (newRegion.trim() && !data.regions.includes(newRegion.trim())) {
      setData({
        ...data,
        regions: [...data.regions, newRegion.trim()]
      });
      setNewRegion('');
    }
  };
  const handleRemoveRegion = (region: string) => {
    setData({
      ...data,
      regions: data.regions.filter(r => r !== region)
    });
  };
  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      // 1. Create Team
      const teamId = teamsApi.generateTeamId(data.teamName);
      const regionRows = data.regions.map(r => ({
        Region_Name: r
      }));
      try {
        await teamsApi.create({
          Team_Name: data.teamName,
          Team_ID: teamId,
          ...(regionRows.length > 0 ? {
            Team_Regions: regionRows
          } : {})
        });
      } catch (teamErr: any) {
        const msg = teamErr?.message || 'Unknown error';
        const debug = teamErr?.debug ? JSON.stringify(teamErr.debug, null, 2) : '';
        console.error('Team creation failed:', teamErr);
        toast.error(`Failed to create team: ${msg}`, {
          description: debug ? `Debug: ${debug.substring(0, 200)}` : undefined,
          duration: 10000
        });
        setIsSubmitting(false);
        return;
      }
      // 2. Create User (Super Admin)
      try {
        await usersApi.create({
          User_Name: data.userName,
          Email: data.userEmail,
          Role: 'Super Admin',
          Status: 'Active',
          Team: data.teamName,
          Invited_By: 'System'
        });
      } catch (userErr: any) {
        const msg = userErr?.message || 'Unknown error';
        const debug = userErr?.debug ? JSON.stringify(userErr.debug, null, 2) : '';
        console.error('User creation failed:', userErr);
        toast.error(`Team created, but user creation failed: ${msg}`, {
          description: debug ? `Debug: ${debug.substring(0, 200)}` : undefined,
          duration: 10000
        });
        setIsSubmitting(false);
        return;
      }
      // 3. Set session and refresh
      localStorage.setItem('configconsole_user_email', data.userEmail);
      await completeOnboarding();
      toast.success('Workspace setup complete!');
    } catch (err: any) {
      console.error('Onboarding failed:', err);
      const msg = err?.message || 'Unknown error';
      toast.error(`Setup failed: ${msg}`, {
        duration: 10000
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  // Progress indicator
  const steps: Step[] = ['welcome', 'user', 'team', 'regions', 'review'];
  const currentStepIndex = steps.indexOf(step);
  const progress = currentStepIndex / (steps.length - 1) * 100;
  return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-id="element-5508">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" data-id="element-5509">
        {/* Progress Bar */}
        <div className="h-1 bg-gray-100 w-full" data-id="element-5510">
          <div className="h-full bg-blue-600 transition-all duration-500 ease-out" style={{
          width: `${progress}%`
        }} data-id="element-5511" />
        </div>

        <div className="p-8" data-id="element-5512">
          {/* Header */}
          <div className="flex items-center justify-between mb-8" data-id="element-5513">
            <div className="flex items-center gap-2" data-id="element-5514">
              <div className="h-8 w-8 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-5515">
                <FileCode className="h-5 w-5 text-blue-600" data-id="element-5516" />
              </div>
              <span className="font-semibold text-gray-900" data-id="element-5517">ConfigConsole</span>
            </div>
            {step !== 'welcome' && <span className="text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-5518">
                Step {currentStepIndex} of 4
              </span>}
          </div>

          {/* Step Content */}
          <div className="min-h-[300px] flex flex-col" data-id="element-5519">
            {step === 'welcome' && <div className="flex-1 flex flex-col items-center text-center justify-center animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5520">
                <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-6" data-id="element-5521">
                  <Building2 className="h-8 w-8 text-blue-600" data-id="element-5522" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-5523">
                  Welcome to ConfigConsole
                </h1>
                <p className="text-gray-500 mb-8 max-w-sm" data-id="element-5524">
                  Let's get your workspace set up. We'll create your admin
                  account and configure your first team.
                </p>
                <button onClick={handleNext} className="w-full bg-gray-900 text-white rounded-lg px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2" data-id="element-5525">
                  Get Started
                  <ArrowRight className="h-4 w-4" data-id="element-5526" />
                </button>
              </div>}

            {step === 'user' && <div className="flex-1 animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-5527">
                <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5528">
                  Create your account
                </h2>
                <p className="text-sm text-gray-500 mb-6" data-id="element-5529">
                  You'll be the Super Admin for this workspace.
                </p>
                <div className="space-y-4" data-id="element-5530">
                  <div data-id="element-5531">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5532">
                      Full Name
                    </label>
                    <div className="relative" data-id="element-5533">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5534" />
                      <input type="text" className="block w-full pl-10 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2.5" placeholder="e.g. John Doe" value={data.userName} onChange={e => setData({
                    ...data,
                    userName: e.target.value
                  })} autoFocus data-id="element-5535" />
                    </div>
                  </div>
                  <div data-id="element-5536">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5537">
                      Email Address
                    </label>
                    <div className="relative" data-id="element-5538">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5539">
                        @
                      </div>
                      <input type="email" className="block w-full pl-10 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2.5" placeholder="john@company.com" value={data.userEmail} onChange={e => setData({
                    ...data,
                    userEmail: e.target.value
                  })} data-id="element-5540" />
                    </div>
                  </div>
                </div>
              </div>}

            {step === 'team' && <div className="flex-1 animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-5541">
                <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5542">
                  Create your first team
                </h2>
                <p className="text-sm text-gray-500 mb-6" data-id="element-5543">
                  Teams help organize your workspace. You can add more later.
                </p>
                <div data-id="element-5544">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5545">
                    Team Name
                  </label>
                  <div className="relative" data-id="element-5546">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5547" />
                    <input type="text" className="block w-full pl-10 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2.5" placeholder="e.g. Marketing, Global, North America" value={data.teamName} onChange={e => setData({
                  ...data,
                  teamName: e.target.value
                })} autoFocus data-id="element-5548" />
                  </div>
                </div>
              </div>}

            {step === 'regions' && <div className="flex-1 animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-5549">
                <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5550">
                  Add regions (Optional)
                </h2>
                <p className="text-sm text-gray-500 mb-6" data-id="element-5551">
                  Define geographic or logical regions for this team.
                </p>

                <form onSubmit={handleAddRegion} className="flex gap-2 mb-4" data-id="element-5552">
                  <div className="relative flex-1" data-id="element-5553">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5554" />
                    <input type="text" className="block w-full pl-10 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2.5" placeholder="e.g. New York, London" value={newRegion} onChange={e => setNewRegion(e.target.value)} data-id="element-5555" />
                  </div>
                  <button type="submit" disabled={!newRegion.trim()} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 font-medium text-sm" data-id="element-5556">
                    Add
                  </button>
                </form>

                <div className="space-y-2 max-h-48 overflow-y-auto" data-id="element-5557">
                  {data.regions.length === 0 ? <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg" data-id="element-5558">
                      <p className="text-sm text-gray-400" data-id="element-5559">
                        No regions added yet
                      </p>
                    </div> : data.regions.map(region => <div key={region} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-5560">
                        <span className="text-sm font-medium text-gray-700" data-id="element-5561">
                          {region}
                        </span>
                        <button onClick={() => handleRemoveRegion(region)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-5562">
                          <X className="h-4 w-4" data-id="element-5563" />
                        </button>
                      </div>)}
                </div>
              </div>}

            {step === 'review' && <div className="flex-1 animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-5564">
                <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5565">
                  Review & Complete
                </h2>
                <p className="text-sm text-gray-500 mb-6" data-id="element-5566">
                  Double check your details before we set everything up.
                </p>

                <div className="space-y-4 bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-5567">
                  <div className="flex items-start gap-3" data-id="element-5568">
                    <div className="h-8 w-8 bg-white rounded-full flex items-center justify-center border border-gray-200 flex-shrink-0" data-id="element-5569">
                      <User className="h-4 w-4 text-gray-500" data-id="element-5570" />
                    </div>
                    <div data-id="element-5571">
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide" data-id="element-5572">
                        Admin Account
                      </p>
                      <p className="text-sm font-medium text-gray-900" data-id="element-5573">
                        {data.userName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-5574">{data.userEmail}</p>
                    </div>
                  </div>

                  <div className="h-px bg-gray-200" data-id="element-5575" />

                  <div className="flex items-start gap-3" data-id="element-5576">
                    <div className="h-8 w-8 bg-white rounded-full flex items-center justify-center border border-gray-200 flex-shrink-0" data-id="element-5577">
                      <Building2 className="h-4 w-4 text-gray-500" data-id="element-5578" />
                    </div>
                    <div data-id="element-5579">
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide" data-id="element-5580">
                        Team
                      </p>
                      <p className="text-sm font-medium text-gray-900" data-id="element-5581">
                        {data.teamName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-5582">
                        {data.regions.length === 0 ? 'No regions defined' : `${data.regions.length} region${data.regions.length === 1 ? '' : 's'}: ${data.regions.join(', ')}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
          </div>

          {/* Footer Navigation */}
          {step !== 'welcome' && <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100" data-id="element-5583">
              <button onClick={handleBack} className="text-sm font-medium text-gray-500 hover:text-gray-900 flex items-center gap-1" data-id="element-5584">
                <ChevronLeft className="h-4 w-4" data-id="element-5585" />
                Back
              </button>

              {step === 'review' ? <button onClick={handleComplete} disabled={isSubmitting} className="bg-blue-600 text-white rounded-lg px-6 py-2.5 text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-70" data-id="element-5586">
                  {isSubmitting ? <>
                      <Loader2 className="h-4 w-4 animate-spin" data-id="element-5587" />
                      Setting up...
                    </> : <>
                      Complete Setup
                      <Check className="h-4 w-4" data-id="element-5588" />
                    </>}
                </button> : <button onClick={handleNext} disabled={step === 'user' && (!data.userName || !data.userEmail) || step === 'team' && !data.teamName} className="bg-gray-900 text-white rounded-lg px-6 py-2.5 text-sm font-medium hover:bg-gray-800 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5589">
                  Next Step
                  <ArrowRight className="h-4 w-4" data-id="element-5590" />
                </button>}
            </div>}
        </div>
      </div>
    </div>;
}